/**
 * Turning eight numbers into a suggestion a person can argue with.
 *
 * Three outcomes, and two of them are non-answers on purpose. A named shape is offered only when one rule matches well and clearly better than the next; when two are close the answer is MIXED and both are kept; when nothing matches well, or the geometry was refused, the answer is UNKNOWN and no shape is invented.
 *
 * Nothing here confirms anything. What comes out is `suggested`, which an optician then accepts, changes or rejects, and no score at any level promotes it on its own.
 */
import { extractFeatures, type FaceFeatures } from './features';
import { checkGeometry, GATES, type GateConfig, type GeometryQuality } from './gates';
import { normalise } from './normalise';
import { outlineFrom, type Outline } from './oval';
import { widthProfile, type WidthProfile } from './profile';
import { ReasonCode } from './reasons';
import { scoreShapes, type ShapeScore } from './rules';
import { FaceShape, type NamedFaceShape } from './shapes';
import { VERSIONS, type FaceVisionVersions } from './versions';
import type { FaceLandmarks } from '@optiflow/pd-vision';

export interface DecisionConfig {
  /** Below this, the best rule has not matched well enough to name a shape. */
  clearFloorBp: number;
  /** The best must beat the runner-up by at least this, or the two are called MIXED. */
  clearMarginBp: number;
}

/** Provisional, like the gates: versioned with the classifier and reported, never presented as settled. */
export const DECISION: DecisionConfig = {
  clearFloorBp: 5_000,
  clearMarginBp: 1_500,
};

export const DecisionBand = {
  /** One rule matched well and clearly better than the next. */
  CLEAR: 'CLEAR',
  /** Two rules matched well and close together. */
  CLOSE: 'CLOSE',
  /** Nothing matched well enough, or the geometry was refused. */
  WEAK: 'WEAK',
} as const;
export type DecisionBand = (typeof DecisionBand)[keyof typeof DecisionBand];

export interface FaceAnalysis {
  /** What to offer the optician. Never written anywhere as a confirmed value. */
  suggested: FaceShape;
  /** For MIXED, the two shapes it sits between, best first. Empty otherwise. */
  components: NamedFaceShape[];
  /** The next best shape when a single one was named; null otherwise. */
  runnerUp: NamedFaceShape | null;
  band: DecisionBand;
  /** Every shape's independent rule-match score, in fixed shape order. These do not sum to 10 000. */
  scores: ShapeScore[];
  topScoreBp: number;
  runnerUpScoreBp: number;
  marginBp: number;
  reasons: ReasonCode[];
  geometry: GeometryQuality;
  /** Null when the geometry could not be measured at all. */
  features: FaceFeatures | null;
  versions: FaceVisionVersions;
}

/** The reasons behind a suggestion, read off the features rather than the winning rule, so an overruled suggestion still explains itself. */
export function reasonsFor(f: FaceFeatures): ReasonCode[] {
  const out: ReasonCode[] = [];

  if (f.lengthToWidth >= 1.34) out.push(ReasonCode.FACE_CLEARLY_LONGER_THAN_WIDE);
  else if (f.lengthToWidth >= 1.14) out.push(ReasonCode.FACE_LONGER_THAN_WIDE);
  else out.push(ReasonCode.FACE_AS_WIDE_AS_LONG);

  out.push(f.sideStraightness <= 0.04 ? ReasonCode.STRAIGHT_SIDES : ReasonCode.CURVED_SIDES);
  out.push(f.jawAngleDeg <= 148 ? ReasonCode.ANGULAR_JAW : ReasonCode.SOFT_JAW);

  if (f.jawToForehead >= 1.06) out.push(ReasonCode.WIDENS_TOWARDS_JAW);
  else if (f.jawToForehead <= 0.86) out.push(ReasonCode.NARROWS_TOWARDS_CHIN);

  if (f.foreheadToWidest >= 0.95) out.push(ReasonCode.WIDE_FOREHEAD);
  else if (f.foreheadToWidest <= 0.85) out.push(ReasonCode.NARROW_FOREHEAD);
  else out.push(ReasonCode.FOREHEAD_BALANCED_WITH_CHEEKS);

  if (f.jawToWidest >= 0.92) out.push(ReasonCode.WIDE_JAW);
  else if (f.jawToWidest <= 0.8) out.push(ReasonCode.NARROW_JAW);

  if (f.widestAt >= 0.38 && f.widestAt <= 0.6) out.push(ReasonCode.WIDEST_AT_CHEEKBONES);

  out.push(f.chinRoundness <= 0.6 ? ReasonCode.POINTED_CHIN : ReasonCode.BROAD_CHIN);

  return out;
}

/** The decision, given scores from a geometry that already passed its gates. */
export function decide(
  scores: ShapeScore[],
  config: DecisionConfig = DECISION,
): Pick<
  FaceAnalysis,
  'suggested' | 'components' | 'runnerUp' | 'band' | 'topScoreBp' | 'runnerUpScoreBp' | 'marginBp'
> {
  const ranked = [...scores].sort(
    (a, b) => b.scoreBp - a.scoreBp || a.shape.localeCompare(b.shape),
  );
  const top = ranked[0]!;
  const second = ranked[1]!;
  const margin = top.scoreBp - second.scoreBp;

  if (top.scoreBp < config.clearFloorBp) {
    return {
      suggested: FaceShape.UNKNOWN,
      components: [],
      runnerUp: null,
      band: DecisionBand.WEAK,
      topScoreBp: top.scoreBp,
      runnerUpScoreBp: second.scoreBp,
      marginBp: margin,
    };
  }

  if (margin < config.clearMarginBp) {
    return {
      suggested: FaceShape.MIXED,
      components: [top.shape, second.shape],
      runnerUp: null,
      band: DecisionBand.CLOSE,
      topScoreBp: top.scoreBp,
      runnerUpScoreBp: second.scoreBp,
      marginBp: margin,
    };
  }

  return {
    suggested: top.shape,
    components: [],
    runnerUp: second.shape,
    band: DecisionBand.CLEAR,
    topScoreBp: top.scoreBp,
    runnerUpScoreBp: second.scoreBp,
    marginBp: margin,
  };
}

export interface AnalyseInput {
  /** Every face the landmarker found. More than one is refused: which face is being advised must never be guessed. */
  faces: readonly FaceLandmarks[];
  frameWidth: number;
  frameHeight: number;
  /** Head pose from the landmarker, when available. Null leaves those gates unchecked, which the report must say. */
  yawDeg: number | null;
  pitchDeg: number | null;
  gates?: GateConfig;
  decision?: DecisionConfig;
}

const refused = (geometry: GeometryQuality): FaceAnalysis => ({
  suggested: FaceShape.UNKNOWN,
  components: [],
  runnerUp: null,
  band: DecisionBand.WEAK,
  scores: [],
  topScoreBp: 0,
  runnerUpScoreBp: 0,
  marginBp: 0,
  reasons: [],
  geometry,
  features: null,
  versions: VERSIONS,
});

const noGeometry = (failure: GeometryQuality['failures'][number]): FaceAnalysis =>
  refused({
    failures: [failure],
    measured: {
      yawDeg: null,
      pitchDeg: null,
      rollAppliedDeg: 0,
      faceHeightPx: 0,
      asymmetry: 1,
      marginShare: 0,
    },
  });

/** One frame, end to end. The landmarks are read and dropped; nothing here keeps a point past the call. */
export function analyseFaceShape(input: AnalyseInput): FaceAnalysis {
  if (input.faces.length === 0) return noGeometry('NO_FACE');
  if (input.faces.length > 1) return noGeometry('MULTIPLE_FACES');

  const raw = outlineFrom(input.faces[0]!, input.frameWidth, input.frameHeight);
  if (!raw) return noGeometry('INCOMPLETE_OUTLINE');

  return analyseOutline(raw, input);
}

/** The same analysis from an outline that is already in pixels, which is what the synthetic tests drive. */
export function analyseOutline(raw: Outline, input: Omit<AnalyseInput, 'faces'>): FaceAnalysis {
  const { outline, rollAppliedDeg, midX } = normalise(raw);
  const { profile, measurable } = widthProfile(outline, midX);

  const geometry = checkGeometry(
    {
      outline,
      profile,
      measurable,
      rollAppliedDeg,
      yawDeg: input.yawDeg,
      pitchDeg: input.pitchDeg,
      frameWidth: input.frameWidth,
      frameHeight: input.frameHeight,
    },
    input.gates ?? GATES,
  );

  if (!measurable) return refused(geometry);

  const features = extractFeatures(profile);
  if (geometry.failures.length > 0) return { ...refused(geometry), features };

  const scores = scoreShapes(features);
  const decision = decide(scores, input.decision ?? DECISION);

  return {
    ...decision,
    scores,
    reasons: reasonsFor(features),
    geometry,
    features,
    versions: VERSIONS,
  };
}

export type { WidthProfile };
